import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, Map } from '../components/Icons.jsx';

const questions = [
  { id: 'postcode', kind: 'postcode', prompt: 'Your home postcode (we use this to show real distance to each medical school).' },
  { id: 'city', prompt: 'What city scale feels right?', options: [
    { value: 'big', label: 'Big city energy (London, Manchester, Edinburgh)' },
    { value: 'mid', label: 'Mid-size & cohesive (Bristol, Leeds, Sheffield)' },
    { value: 'small', label: 'Smaller / campus-feel (Lancaster, Hull, Exeter)' },
    { value: 'any', label: 'No preference' }
  ]},
  { id: 'country', prompt: 'Country preference?', options: [
    { value: 'england', label: 'England' },
    { value: 'scotland', label: 'Scotland' },
    { value: 'wales', label: 'Wales' },
    { value: 'any', label: 'No preference' }
  ]},
  { id: 'style', prompt: "Course style you'd thrive in?", options: [
    { value: 'pbl', label: 'Problem-based learning (work in small groups on cases)' },
    { value: 'traditional', label: 'Traditional lecture-led' },
    { value: 'integrated', label: 'Integrated (mix of both)' },
    { value: 'unsure', label: 'Unsure — open to either' }
  ]},
  { id: 'size', prompt: 'Cohort size you want?', options: [
    { value: 'small', label: 'Small (< 200/year)' },
    { value: 'medium', label: 'Medium (200–300/year)' },
    { value: 'large', label: 'Large (300+/year)' },
    { value: 'any', label: 'No preference' }
  ]},
  { id: 'clinical', prompt: 'When do you want first patient contact?', options: [
    { value: 'early', label: 'Year 1 — patients from day one' },
    { value: 'mid', label: 'Year 2' },
    { value: 'later', label: 'Year 3+ (build basics first)' },
    { value: 'any', label: 'No preference' }
  ]},
  { id: 'interview', prompt: 'Interview style you handle best?', options: [
    { value: 'mmi', label: 'MMI (multiple short stations)' },
    { value: 'panel', label: 'Panel interview (1 long conversation)' },
    { value: 'none', label: 'Prefer no interview (Edinburgh)' },
    { value: 'any', label: 'No preference' }
  ]},
  { id: 'research', prompt: 'Research / intercalated degree importance?', options: [
    { value: 'high', label: 'High — want strong research culture' },
    { value: 'medium', label: 'Nice to have' },
    { value: 'low', label: 'Not important' }
  ]},
  { id: 'distance', prompt: 'How far from home would you go?', options: [
    { value: 'close', label: 'Stay near home (≤ 100 km)' },
    { value: 'med', label: 'A few hours away (100–300 km)' },
    { value: 'far', label: 'Far — fresh start (300 km+)' },
    { value: 'any', label: 'No preference' }
  ]},
  { id: 'aesthetic', prompt: 'Campus feel?', options: [
    { value: 'historic', label: 'Historic / collegiate (Oxford, Cambridge, Edinburgh)' },
    { value: 'modern', label: 'Modern facilities (Plymouth, UEA)' },
    { value: 'mixed', label: 'Mix is fine' },
    { value: 'any', label: 'No preference' }
  ]},
  { id: 'wp', prompt: 'Widening-participation friendly programmes?', options: [
    { value: 'yes', label: 'Yes — important for me' },
    { value: 'no', label: 'Not relevant for me' }
  ]}
];

const schools = [
  { name: 'University of Edinburgh', city: 'Edinburgh', country: 'scotland', cityScale: 'big', style: 'traditional', size: 'medium', clinical: 'later', interview: 'none', research: 'high', aesthetic: 'historic', wp: 'no', lat: 55.95, lng: -3.19 },
  { name: 'University of Manchester', city: 'Manchester', country: 'england', cityScale: 'big', style: 'pbl', size: 'large', clinical: 'mid', interview: 'mmi', research: 'high', aesthetic: 'modern', wp: 'yes', lat: 53.48, lng: -2.24 },
  { name: 'University of Bristol', city: 'Bristol', country: 'england', cityScale: 'mid', style: 'integrated', size: 'medium', clinical: 'mid', interview: 'mmi', research: 'high', aesthetic: 'mixed', wp: 'yes', lat: 51.45, lng: -2.59 },
  { name: 'University of Leeds', city: 'Leeds', country: 'england', cityScale: 'mid', style: 'integrated', size: 'large', clinical: 'mid', interview: 'mmi', research: 'medium', aesthetic: 'mixed', wp: 'yes', lat: 53.80, lng: -1.54 },
  { name: "King's College London (GKT)", city: 'London', country: 'england', cityScale: 'big', style: 'integrated', size: 'large', clinical: 'mid', interview: 'mmi', research: 'high', aesthetic: 'historic', wp: 'no', lat: 51.51, lng: -0.12 },
  { name: 'University College London (UCL)', city: 'London', country: 'england', cityScale: 'big', style: 'integrated', size: 'large', clinical: 'mid', interview: 'mmi', research: 'high', aesthetic: 'historic', wp: 'no', lat: 51.52, lng: -0.13 },
  { name: 'Imperial College London', city: 'London', country: 'england', cityScale: 'big', style: 'traditional', size: 'medium', clinical: 'mid', interview: 'mmi', research: 'high', aesthetic: 'modern', wp: 'no', lat: 51.50, lng: -0.18 },
  { name: 'Queen Mary (Barts)', city: 'London', country: 'england', cityScale: 'big', style: 'pbl', size: 'large', clinical: 'early', interview: 'mmi', research: 'high', aesthetic: 'mixed', wp: 'yes', lat: 51.52, lng: -0.04 },
  { name: 'University of Birmingham', city: 'Birmingham', country: 'england', cityScale: 'big', style: 'integrated', size: 'large', clinical: 'mid', interview: 'mmi', research: 'high', aesthetic: 'modern', wp: 'yes', lat: 52.48, lng: -1.90 },
  { name: 'University of Sheffield', city: 'Sheffield', country: 'england', cityScale: 'mid', style: 'integrated', size: 'medium', clinical: 'mid', interview: 'mmi', research: 'medium', aesthetic: 'mixed', wp: 'yes', lat: 53.38, lng: -1.47 },
  { name: 'Newcastle University', city: 'Newcastle', country: 'england', cityScale: 'mid', style: 'integrated', size: 'medium', clinical: 'early', interview: 'mmi', research: 'high', aesthetic: 'mixed', wp: 'yes', lat: 54.97, lng: -1.61 },
  { name: 'University of Nottingham', city: 'Nottingham', country: 'england', cityScale: 'mid', style: 'integrated', size: 'large', clinical: 'mid', interview: 'mmi', research: 'medium', aesthetic: 'modern', wp: 'yes', lat: 52.95, lng: -1.15 },
  { name: 'University of Southampton', city: 'Southampton', country: 'england', cityScale: 'mid', style: 'integrated', size: 'medium', clinical: 'early', interview: 'mmi', research: 'medium', aesthetic: 'modern', wp: 'yes', lat: 50.91, lng: -1.40 },
  { name: 'University of Liverpool', city: 'Liverpool', country: 'england', cityScale: 'mid', style: 'integrated', size: 'large', clinical: 'mid', interview: 'mmi', research: 'medium', aesthetic: 'mixed', wp: 'yes', lat: 53.40, lng: -2.99 },
  { name: 'University of Leicester', city: 'Leicester', country: 'england', cityScale: 'mid', style: 'integrated', size: 'medium', clinical: 'early', interview: 'mmi', research: 'medium', aesthetic: 'modern', wp: 'yes', lat: 52.63, lng: -1.13 },
  { name: 'Cardiff University', city: 'Cardiff', country: 'wales', cityScale: 'mid', style: 'integrated', size: 'large', clinical: 'mid', interview: 'mmi', research: 'medium', aesthetic: 'mixed', wp: 'yes', lat: 51.48, lng: -3.18 },
  { name: 'University of Glasgow', city: 'Glasgow', country: 'scotland', cityScale: 'big', style: 'pbl', size: 'large', clinical: 'mid', interview: 'mmi', research: 'high', aesthetic: 'historic', wp: 'no', lat: 55.86, lng: -4.25 },
  { name: 'University of Dundee', city: 'Dundee', country: 'scotland', cityScale: 'small', style: 'integrated', size: 'small', clinical: 'early', interview: 'mmi', research: 'medium', aesthetic: 'modern', wp: 'yes', lat: 56.46, lng: -2.97 },
  { name: 'University of Aberdeen', city: 'Aberdeen', country: 'scotland', cityScale: 'mid', style: 'integrated', size: 'medium', clinical: 'mid', interview: 'mmi', research: 'medium', aesthetic: 'historic', wp: 'no', lat: 57.15, lng: -2.10 },
  { name: 'University of Exeter', city: 'Exeter', country: 'england', cityScale: 'small', style: 'integrated', size: 'small', clinical: 'early', interview: 'mmi', research: 'medium', aesthetic: 'mixed', wp: 'no', lat: 50.72, lng: -3.53 },
  { name: 'University of Plymouth (Peninsula)', city: 'Plymouth', country: 'england', cityScale: 'small', style: 'pbl', size: 'medium', clinical: 'early', interview: 'mmi', research: 'medium', aesthetic: 'modern', wp: 'yes', lat: 50.37, lng: -4.14 },
  { name: 'Hull York Medical School', city: 'Hull/York', country: 'england', cityScale: 'small', style: 'pbl', size: 'small', clinical: 'early', interview: 'mmi', research: 'medium', aesthetic: 'mixed', wp: 'yes', lat: 53.96, lng: -1.08 },
  { name: 'University of East Anglia', city: 'Norwich', country: 'england', cityScale: 'small', style: 'pbl', size: 'small', clinical: 'early', interview: 'mmi', research: 'medium', aesthetic: 'modern', wp: 'yes', lat: 52.63, lng: 1.30 },
  { name: 'University of Lancaster', city: 'Lancaster', country: 'england', cityScale: 'small', style: 'pbl', size: 'small', clinical: 'early', interview: 'mmi', research: 'medium', aesthetic: 'modern', wp: 'no', lat: 54.05, lng: -2.80 },
  { name: 'St George\'s, London', city: 'London', country: 'england', cityScale: 'big', style: 'integrated', size: 'medium', clinical: 'early', interview: 'mmi', research: 'medium', aesthetic: 'mixed', wp: 'yes', lat: 51.43, lng: -0.18 }
];

function haversineKm(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(h)));
}

const distanceBuckets = { close: 100, med: 300, far: 9999 };

function scoreMatch(school, answers, homeCoords) {
  let score = 0;
  let max = 0;
  const add = (a, b, weight = 10) => { max += weight; if (answers[a] === school[b] || answers[a] === 'any') score += weight; };
  add('city', 'cityScale');
  add('country', 'country');
  add('style', 'style');
  add('size', 'size');
  add('clinical', 'clinical');
  add('interview', 'interview');
  if (answers.research && school.research) { max += 10; if (answers.research === school.research || (answers.research === 'medium' && school.research !== 'low')) score += 10; }
  add('aesthetic', 'aesthetic');
  add('wp', 'wp');

  if (answers.distance && answers.distance !== 'any' && homeCoords) {
    max += 10;
    const km = haversineKm(homeCoords, school);
    const ceil = distanceBuckets[answers.distance];
    if (answers.distance === 'far' && km >= 300) score += 10;
    else if (km <= ceil) score += 10;
  } else if (answers.distance && answers.distance !== 'any') {
    add('distance', '__noop__', 0);
  }
  return Math.round((score / max) * 100);
}

export default function SchoolMatcher() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [homeCoords, setHomeCoords] = useState(null);
  const [postcodeInput, setPostcodeInput] = useState('');
  const [postcodeStatus, setPostcodeStatus] = useState({ state: 'idle', message: '' });

  const submitPostcode = async () => {
    const raw = postcodeInput.trim();
    if (!raw) {
      setPostcodeStatus({ state: 'error', message: 'Enter a postcode or tap "Skip — N/A".' });
      return;
    }
    setPostcodeStatus({ state: 'loading', message: '' });
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(raw)}`);
      if (res.status === 404) {
        setPostcodeStatus({ state: 'error', message: "We couldn't find that postcode. Check it, or tap Skip." });
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const lat = data?.result?.latitude;
      const lng = data?.result?.longitude;
      if (typeof lat !== 'number' || typeof lng !== 'number') {
        setPostcodeStatus({ state: 'error', message: "Couldn't read coordinates for that postcode. Try again or skip." });
        return;
      }
      setHomeCoords({ lat, lng });
      setAnswers({ ...answers, postcode: raw.toUpperCase() });
      setPostcodeStatus({ state: 'idle', message: '' });
      setStep(step + 1);
    } catch (e) {
      setPostcodeStatus({ state: 'error', message: 'Network issue looking up that postcode. Try again or skip.' });
    }
  };

  const skipPostcode = () => {
    setHomeCoords(null);
    setAnswers({ ...answers, postcode: 'N/A' });
    setPostcodeStatus({ state: 'idle', message: '' });
    setStep(step + 1);
  };

  const ranked = useMemo(() => {
    if (step < questions.length) return [];
    return [...schools]
      .map((s) => ({
        ...s,
        match: scoreMatch(s, answers, homeCoords),
        distanceKm: homeCoords ? haversineKm(homeCoords, s) : null
      }))
      .sort((a, b) => b.match - a.match);
  }, [step, answers, homeCoords]);

  if (step >= questions.length) {
    const top = ranked.slice(0, 8);
    return (
      <>
        <div className="page-head">
          <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>Your matches</div>
          <h1>Top medical schools for you</h1>
          <p className="subtitle">Based on your preferences only — not your UCAT score. Use the <Link to="/where-can-i-apply" style={{ color: 'var(--accent)', fontWeight: 600 }}>Where can I apply</Link> tool to filter by score too.</p>
          {answers.postcode && answers.postcode !== 'N/A' && (
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 8 }}>Distances measured from <strong>{answers.postcode}</strong> · straight-line (Haversine), not road distance.</p>
          )}
        </div>

        <div className="parents-grid">
          {top.map((s, i) => (
            <div key={s.name} className="parents-card" style={{ borderLeft: i < 3 ? '4px solid var(--brand-purple)' : '4px solid transparent' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>#{i + 1}</span>
                <span className="pill pill-teal" style={{ fontSize: 13 }}>{s.match}% match</span>
              </div>
              <h3 style={{ marginTop: 8 }}>{s.name}</h3>
              <p style={{ margin: '4px 0 0', color: 'var(--text-muted)' }}>
                {s.city} · {s.style === 'pbl' ? 'PBL' : s.style === 'traditional' ? 'Traditional' : 'Integrated'} · {s.size} cohort
              </p>
              {s.distanceKm != null && (
                <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--brand-purple)', fontWeight: 600 }}>
                  ~{s.distanceKm} km from home
                </p>
              )}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
          <button onClick={() => { setStep(0); setAnswers({}); setHomeCoords(null); setPostcodeInput(''); }} className="btn btn-secondary">Retake quiz</button>
          <Link to="/where-can-i-apply" className="btn btn-primary">Now check by UCAT score <ArrowRight width="16" height="16" /></Link>
        </div>

        <section className="parents-cta" style={{ marginTop: 32 }}>
          <div>
            <h2>Not sure how to pick four from this list?</h2>
            <p>A Medic Mind advisor will help you build a balanced UCAS shortlist — one stretch, two competitive, one safe — based on your scores and preferences. Free.</p>
          </div>
          <a className="btn" href="https://www.medicmind.co.uk/contact-us/" target="_blank" rel="noreferrer">Book free call <ArrowRight width="16" height="16" /></a>
        </section>
      </>
    );
  }

  const q = questions[step];
  const progress = ((step + 1) / questions.length) * 100;

  return (
    <>
      <div className="page-head">
        <div className="kicker" style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 600 }}>Question {step + 1} of {questions.length}</div>
        <h1>School matcher</h1>
        <p className="subtitle">11 quick questions. ~90 seconds.</p>
      </div>

      <div className="progress" style={{ marginBottom: 20, height: 6 }}>
        <div className="progress-bar" style={{ width: `${progress}%` }} />
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <h2 style={{ margin: '0 0 18px', fontSize: 20 }}>{q.prompt}</h2>

        {q.kind === 'postcode' ? (
          <div>
            <p style={{ margin: '0 0 12px', color: 'var(--text-muted)', fontSize: 14 }}>
              We pass this to <a href="https://postcodes.io/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>postcodes.io</a> (free UK postcode API). Nothing is saved.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input
                type="text"
                value={postcodeInput}
                onChange={(e) => setPostcodeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitPostcode(); }}
                placeholder="e.g. SW1A 1AA"
                autoFocus
                style={{
                  flex: 1, minWidth: 200, padding: '12px 16px', fontSize: 16, fontWeight: 600,
                  border: '1px solid var(--border-strong)', borderRadius: 12, textTransform: 'uppercase'
                }}
              />
              <button
                onClick={submitPostcode}
                disabled={postcodeStatus.state === 'loading'}
                className="btn btn-primary"
              >
                {postcodeStatus.state === 'loading' ? 'Checking…' : 'Continue'} <ArrowRight width="16" height="16" />
              </button>
              <button
                onClick={skipPostcode}
                className="btn btn-secondary"
                disabled={postcodeStatus.state === 'loading'}
              >Skip — N/A</button>
            </div>
            {postcodeStatus.state === 'error' && (
              <p style={{ marginTop: 10, color: '#b91c1c', fontSize: 13 }}>{postcodeStatus.message}</p>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {q.options.map((opt) => (
              <button
                key={opt.value}
                onClick={() => { setAnswers({ ...answers, [q.id]: opt.value }); setStep(step + 1); }}
                style={{
                  padding: '14px 18px', textAlign: 'left',
                  border: `1px solid ${answers[q.id] === opt.value ? 'var(--brand-purple)' : 'var(--border)'}`,
                  background: answers[q.id] === opt.value ? 'var(--accent-soft)' : '#fff',
                  color: 'var(--text)', borderRadius: 12, fontSize: 15, fontWeight: 500,
                  cursor: 'pointer', transition: 'border-color .15s, background .15s'
                }}
              >{opt.label}</button>
            ))}
          </div>
        )}
      </div>

      {step > 0 && <button onClick={() => setStep(step - 1)} className="back-link"><ArrowLeft width="16" height="16" />Previous</button>}
    </>
  );
}
